import { call, put, SagaGenerator, select, takeLatest } from "typed-redux-saga";
import { Action } from "typescript-fsa";

import { EntityInstancePath } from "@com.mgmtp.a12.kernel/kernel-md-facade";
import { TreeEngineActions, TreeEngineActivity } from "@com.mgmtp.a12.treeengine/treeengine-core/lib/extensions/client";

import { getLatestFVManifests } from "../../../app/slices/fachverfahren/selectors";
import { RuntimeFachverfahrenManifest } from "../../../app/slices/fachverfahren/types";
import { getLatestSDManifests } from "../../../app/slices/stammdaten/selectors";
import { RuntimeStammdatenManifest } from "../../../app/slices/stammdaten/types";
import { RESOURCE_KEYS } from "../../../localization";
import { getVirtualVerfahrenDocRef } from "../../../providers/textbausteine/textbausteinDataProviderHelpers";
import { assertNonNullable } from "../../../utilities/assert";
import { putErrorNotification } from "../../../utilities/notificationUtils";
import { parseToStringPathWithIndices } from "../../../utilities/pathHelpers";
import {
	TEXTBAUSTEINE_READONLY_TREE,
	TEXTBAUSTEINE_RELATIONSHIP_DOCUMENT
} from "../../../utilities/variables/documentModelName";

import { openTextTree, OpenTextTreePayload } from "./actions";

/**
 * Saga um den Textbausteinbaum aus dem Formular zu öffnen
 * um Texte einzufügen
 */
export function* openTreeSaga(): SagaGenerator<void> {
	yield* takeLatest(openTextTree.match, handle);
}

type EventAction = Action<OpenTextTreePayload>;
/**
 * Tries to get the node and opens a modal if a node was found for the given marker.
 * @param action
 */
export function* handle(action: EventAction): SagaGenerator<void> {
	const { fvKey, repeatableGroupPath, initiatingActivityId, kategorie, targetFieldName } = action.payload;

	const latestFVManifests = yield* select(getLatestFVManifests);
	const fvManifest = Object.values(latestFVManifests).find(e => e.identification.key === fvKey);

	if (fvManifest?.textbausteinData) {
		const activityDescriptor = createActivityDescriptor(
			fvManifest,
			repeatableGroupPath,
			kategorie,
			targetFieldName
		);
		yield* put(TreeEngineActions.createActivity({ activityDescriptor, initiatingActivityId }));
		return;
	}

	const latestSDManifests = yield* select(getLatestSDManifests);
	const sdManifest = Object.values(latestSDManifests).find(e => e.identification.key === fvKey);

	if (sdManifest?.textbausteinData) {
		const activityDescriptor = createSDActivityDescriptor(
			sdManifest,
			repeatableGroupPath,
			kategorie,
			targetFieldName
		);
		yield* put(TreeEngineActions.createActivity({ activityDescriptor, initiatingActivityId }));
		return;
	}

	yield* call(
		putErrorNotification,
		RESOURCE_KEYS.features.notification.error.textbausteine.openModal,
		"Could not find fitting manifest for key " + fvKey,
		undefined,
		`openTree:${fvKey}`
	);
}

function createActivityDescriptor(
	fachverfahrenManifest: RuntimeFachverfahrenManifest,
	documentPath: EntityInstancePath,
	kategorie?: string,
	targetFieldName?: string
): TreeEngineActivity.Descriptor.HiddenRootInstance & { rootName: string } {
	assertNonNullable(fachverfahrenManifest.textbausteinData);

	return {
		model: TEXTBAUSTEINE_READONLY_TREE,
		engine: "tree",
		modal: "CustomTextbausteineTreeEngine",
		rootInstance: getVirtualVerfahrenDocRef(fachverfahrenManifest),
		rootRelationshipName: TEXTBAUSTEINE_RELATIONSHIP_DOCUMENT,
		rootRelationshipRole: "parent",
		targetDocumentPath: parseToStringPathWithIndices(documentPath),
		rootName: fachverfahrenManifest.textbausteinData.name,
		kategorie,
		targetFieldName
	};
}

function createSDActivityDescriptor(
	stammdatenManifest: RuntimeStammdatenManifest,
	documentPath: EntityInstancePath,
	kategorie?: string,
	targetFieldName?: string
): TreeEngineActivity.Descriptor.HiddenRootInstance & { rootName: string } {
	assertNonNullable(stammdatenManifest.textbausteinData);

	return {
		model: TEXTBAUSTEINE_READONLY_TREE,
		engine: "tree",
		modal: "CustomTextbausteineTreeEngine",
		rootInstance: getVirtualVerfahrenDocRef(stammdatenManifest),
		rootRelationshipName: TEXTBAUSTEINE_RELATIONSHIP_DOCUMENT,
		rootRelationshipRole: "parent",
		targetDocumentPath: parseToStringPathWithIndices(documentPath),
		rootName: stammdatenManifest.textbausteinData.name,
		kategorie,
		targetFieldName
	};
}
